// /wall — the big screen.
//
// This page runs unattended for hours on a TV or projector, so its constraints
// are different from every other route:
//
//  - MEMORY: the photo list is hard-capped. New arrivals push old ones out
//    rather than accumulating, so a six-hour event does not end in a dead tab.
//  - NO FLICKER: signed URLs are held per photo id and only re-signed when they
//    approach expiry. Naively taking the fresh URL from every poll would make
//    the whole wall reload every 20 seconds.
//  - SURVIVES THE NETWORK: a failed poll keeps the current photos on screen.
//    The venue wifi dropping must not blank the wall in front of the room.
//  - NEVER SHOWS AN ERROR: there is no error state. If something is wrong the
//    wall shows the brand card and waits.
import { useCallback, useEffect, useRef, useState } from "react";
import { type WallPhoto, wallFeed } from "../lib/api";

const POLL_MS = 20_000;
// One hero plus eight thumbnails is what the grid actually draws, so that is
// what we ask for. It used to fetch 24 and render 9 — fifteen signed URLs
// minted and thrown away every twenty seconds, all day.
const THUMBS = 8;
const MAX_TILES = 1 + THUMBS;
// Signed URLs live an hour; refresh well before that so a tile never 403s.
const URL_REFRESH_MS = 45 * 60_000;

interface Tile extends WallPhoto {
  signedAt: number;
}

export default function Wall() {
  const [tiles, setTiles] = useState<Tile[]>([]);
  // Held in a ref so the poll callback never needs `tiles` as a dependency —
  // otherwise the interval would be torn down and rebuilt on every update.
  const tilesRef = useRef<Tile[]>([]);
  tilesRef.current = tiles;

  const poll = useCallback(async () => {
    try {
      const res = await wallFeed(MAX_TILES);
      const now = Date.now();
      const existing = new Map(tilesRef.current.map((t) => [t.id, t]));

      const next: Tile[] = res.photos.slice(0, MAX_TILES).map((p) => {
        const prev = existing.get(p.id);
        // Keep the URL we already rendered unless it is close to expiring.
        // Swapping it every poll would reload the image and flash the screen.
        if (prev && now - prev.signedAt < URL_REFRESH_MS) return prev;
        return { ...p, signedAt: now };
      });

      setTiles(next);
    } catch {
      // Keep whatever is on screen. A blank wall is worse than a stale one.
    }
  }, []);

  useEffect(() => {
    void poll();
    const id = setInterval(() => void poll(), POLL_MS);
    return () => clearInterval(id);
  }, [poll]);

  // Keep the screen awake. Best-effort: unsupported browsers just ignore it,
  // and the lock is re-taken if the OS drops it when the tab is backgrounded.
  useEffect(() => {
    let lock: { release: () => Promise<void> } | null = null;
    const nav = navigator as Navigator & {
      wakeLock?: { request(t: "screen"): Promise<{ release: () => Promise<void> }> };
    };

    const take = () => {
      nav.wakeLock?.request("screen")
        .then((l) => {
          lock = l;
        })
        .catch(() => {/* not supported, or denied — the venue can disable sleep */});
    };

    take();
    const onVisible = () => {
      if (document.visibilityState === "visible") take();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      void lock?.release().catch(() => {});
    };
  }, []);

  // Standby covers BOTH "still loading" and "genuinely empty".
  //
  // This previously gated on everLoaded, which meant the very first render —
  // before any poll returned — fell through to Grid with an empty array, and
  // Grid's `const [hero, ...rest] = tiles` made hero undefined. The wall
  // crashed to a blank screen on every single startup. A moment of the brand
  // card while the first photos load is exactly what a wall should show anyway.
  const empty = tiles.length === 0;

  return (
    <div dir="ltr" className="relative min-h-dvh overflow-hidden bg-ink text-paper">
      <header className="absolute inset-x-0 top-0 z-10 flex items-baseline
                         justify-between px-8 py-6">
        <div className="flex items-baseline gap-3">
          <span className="text-2xl font-bold tracking-tight">LAQTA</span>
          <span lang="ar" dir="rtl" className="text-2xl font-bold tracking-tight">
            لقطة
          </span>
        </div>
        <span className="text-sm uppercase tracking-[0.3em] text-muted">
          LYNK &amp; CO
        </span>
      </header>

      {empty ? <Standby /> : <Grid tiles={tiles} />}
    </div>
  );
}

/**
 * Shown before the first photo lands, and any time the wall is empty.
 *
 * The copy no longer says "register" — there is nothing to register for. A
 * guest walks up, has their photo taken, and it appears here.
 *
 * Each line carries its own dir and lang. The page is dir="ltr", and an Arabic
 * sentence dropped into an LTR container has its trailing punctuation resolved
 * by the bidi algorithm against the wrong base direction — the comma lands on
 * the wrong end. lang="ar" is also what selects the Arabic face (index.css).
 */
function Standby() {
  return (
    <div className="flex min-h-dvh flex-col items-center justify-center px-8 text-center">
      {/* One line, two scripts, each in its own element. Putting both in a
          single text node leaves the separator's placement to the bidi
          algorithm, which resolves it against the container's LTR base and can
          land it on the wrong side of the Arabic. */}
      <div className="flex items-baseline justify-center gap-4">
        <span lang="ar" dir="rtl" className="text-4xl font-bold tracking-tight sm:text-5xl">
          لقطة
        </span>
        <span aria-hidden="true" className="text-3xl text-muted sm:text-4xl">·</span>
        <span className="text-4xl font-bold tracking-tight sm:text-5xl">LAQTA</span>
      </div>
      {/* Modern Standard Arabic, not colloquial: this is brand signage for a
          premium activation, and it sits next to the English line. */}
      <p lang="ar" dir="rtl" className="mt-6 max-w-xl text-lg text-muted sm:text-xl">
        التقط صورتك معنا، وشاهدها هنا
      </p>
      <p lang="en" dir="ltr" className="mt-2 max-w-xl text-base text-muted sm:text-lg">
        Have your photo taken — watch it appear here
      </p>
    </div>
  );
}

function Grid({ tiles }: { tiles: Tile[] }) {
  // The newest photo gets the hero slot — on a wall, people want to see the
  // shot that was just taken, and a uniform grid buries it.
  const [hero, ...rest] = tiles;
  // Never assume a non-empty list. This screen runs unattended for hours; a
  // crash here is a blank wall in front of the room with nobody at a keyboard.
  if (!hero) return <Standby />;

  return (
    // Three deliberate shapes, because this page is plugged into whatever
    // screen the venue has. Below lg it stacks: hero on top, thumbnails under
    // it, which is what an iPad in portrait (768–834px) gets. At lg and up the
    // hero takes two of three columns with the thumbnails beside it.
    <div className="grid min-h-dvh grid-cols-1 gap-3 p-4 pt-24 lg:grid-cols-3">
      <figure className="relative overflow-hidden rounded-2xl bg-ink-soft lg:col-span-2">
        <img
          key={hero.id}
          src={hero.url}
          alt=""
          className="size-full animate-[fadeIn_700ms_ease-out] object-cover"
        />
        {/* 'external' is an image generated outside this system, so it is an
            AI portrait to everyone looking at the screen. Labelling only
            origin='ai' would leave half the generated shots unmarked. */}
        {hero.origin !== "backdrop" && (
          <figcaption className="absolute bottom-4 start-4 rounded-full bg-ink/80
                                 px-4 py-1.5 text-sm tracking-widest text-accent">
            AI PORTRAIT
          </figcaption>
        )}
      </figure>

      {/* Two up on a phone, wider as the screen allows, and back to two when
          the thumbnails become a narrow side column at lg. The old
          `lg:grid-cols-2` here was dead — identical to the base. */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-2">
        {rest.slice(0, THUMBS).map((t) => (
          <figure
            key={t.id}
            className="overflow-hidden rounded-xl bg-ink-soft"
          >
            <img
              src={t.url}
              alt=""
              loading="lazy"
              decoding="async"
              className="aspect-[3/4] w-full object-cover"
            />
          </figure>
        ))}
      </div>

      <style>
        {`@keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }`}
      </style>
    </div>
  );
}
